import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const videoHeroSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h1: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  video_url: z.string().min(1).max(500),
  poster_url: z.string().max(500).optional(),
  overlay_opacity: z.number().min(0).max(100).default(40).optional(),
  ctas: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        href: z.string().min(1).max(500),
        variant: z.enum(['primary', 'ghost']).default('primary'),
      }),
    )
    .max(2)
    .optional(),
});

export type VideoHeroData = z.infer<typeof videoHeroSchema>;

export function VideoHeroRenderer({ eyebrow, h1, subtitle, video_url, poster_url, overlay_opacity = 40, ctas }: VideoHeroData) {
  return (
    <section className="v5-video-hero">
      <video
        className="v5-video-hero-video"
        src={video_url}
        poster={poster_url}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
      <div className="v5-video-hero-overlay" style={{ background: `rgba(0,0,0,${overlay_opacity / 100})` }} aria-hidden />
      <div className="v5-video-hero-content container">
        {eyebrow ? <div className="v5-eyebrow v5-eyebrow-light">{eyebrow}</div> : null}
        <h1 className="v5-h1-hero">{h1}</h1>
        {subtitle ? <p className="v5-lead-light">{subtitle}</p> : null}
        {Array.isArray(ctas) && ctas.length > 0 ? (
          <div className="v5-hero-ctas">
            {ctas.map((c, i) => (
              <a key={i} href={c.href} className={c.variant === 'ghost' ? 'v5-btn-ghost-light' : 'v5-btn-primary'}>
                {c.label}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export const videoHeroBlock: BlockDefinition<VideoHeroData> = {
  meta: {
    type: 'video-hero',
    label: 'Hero con vídeo de fondo',
    description: 'Hero a pantalla completa con vídeo en loop, overlay y CTAs. Soporta poster como fallback.',
    category: 'hero',
    icon: 'video',
  },
  schema: videoHeroSchema,
  defaults: {
    eyebrow: 'NUEVO',
    h1: 'Vende tu empresa con quien ya ha vendido la suya',
    subtitle: '17 años acompañando founders en operaciones de venta y compra.',
    video_url: '/media/hero-tech.mp4',
    poster_url: '/media/hero-tech.jpg',
    overlay_opacity: 50,
    ctas: [
      { label: 'Habla con un asesor', href: '/contacto', variant: 'primary' },
      { label: 'Ver casos', href: '/portfolio', variant: 'ghost' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h1', label: 'Título H1', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    { name: 'video_url', label: 'URL del vídeo (MP4/WebM)', type: 'url', required: true },
    { name: 'poster_url', label: 'Imagen poster (fallback)', type: 'image' },
    { name: 'overlay_opacity', label: 'Opacidad overlay (0-100)', type: 'number', min: 0, max: 100 },
    {
      name: 'ctas',
      label: 'CTAs (máx 2)',
      type: 'array',
      max: 2,
      itemFields: [
        { name: 'label', label: 'Texto', type: 'text', required: true, max: 80 },
        { name: 'href', label: 'URL', type: 'url', required: true },
        {
          name: 'variant',
          label: 'Estilo',
          type: 'select',
          options: [
            { label: 'Primario', value: 'primary' },
            { label: 'Ghost (transparente)', value: 'ghost' },
          ],
        },
      ],
    },
  ],
  Renderer: VideoHeroRenderer,
};
